import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { cn } from '@/lib/cn';
import { BehaviorTypesPanel } from './panels/BehaviorTypesPanel';
import { ProgramDomainsPanel } from './panels/ProgramDomainsPanel';
import { DiagnosesPanel } from './panels/DiagnosesPanel';
import { ServiceCodesPanel } from './panels/ServiceCodesPanel';
import { ServiceTypesPanel } from './panels/ServiceTypesPanel';
import { SessionTypesPanel } from './panels/SessionTypesPanel';
import { NoteTypesPanel } from './panels/NoteTypesPanel';
import { LocationTypesPanel } from './panels/LocationTypesPanel';
import { CancellationReasonsPanel } from './panels/CancellationReasonsPanel';
import { ParentRelationshipsPanel } from './panels/ParentRelationshipsPanel';
import { ResultCodesPanel } from './panels/ResultCodesPanel';
import { PayersPanel } from './panels/PayersPanel';
import { PracticeInformationPanel } from './panels/PracticeInformationPanel';
import { PracticeHoursPanel } from './panels/PracticeHoursPanel';
import { SubmissionSettingsPanel } from './panels/SubmissionSettingsPanel';
import { BillingSettingsPanel } from './panels/BillingSettingsPanel';
import { SchedulingRulesPanel } from './panels/SchedulingRulesPanel';
import { AuthorizationLimitsPanel } from './panels/AuthorizationLimitsPanel';
import { NotificationsPanel } from './panels/NotificationsPanel';
import {
    ClientCustomFieldsPanel,
    TreatmentPlanCustomFieldsPanel,
} from './panels/CustomFieldsPanel';

type GroupKey =
    | 'practice'
    | 'scheduling'
    | 'sessions'
    | 'clients'
    | 'treatment'
    | 'billing';

interface SubItem {
    key: string;
    label: string;
    panel: React.ComponentType;
}

interface Group {
    key: GroupKey;
    label: string;
    items: SubItem[];
    placeholder?: string;
}

const GROUPS: Group[] = [
    {
        key: 'practice',
        label: 'Practice',
        items: [
            { key: 'practice-information', label: 'Practice Information', panel: PracticeInformationPanel },
            { key: 'notifications', label: 'Notifications', panel: NotificationsPanel },
        ],
    },
    {
        key: 'scheduling',
        label: 'Scheduling',
        items: [
            { key: 'scheduling-rules', label: 'Rules', panel: SchedulingRulesPanel },
            { key: 'practice-hours', label: 'Practice Hours', panel: PracticeHoursPanel },
            { key: 'location-types', label: 'Location Types', panel: LocationTypesPanel },
            { key: 'cancellation-reasons', label: 'Cancellation Reasons', panel: CancellationReasonsPanel },
        ],
    },
    {
        key: 'sessions',
        label: 'Sessions & Notes',
        items: [
            { key: 'service-codes', label: 'Service Codes', panel: ServiceCodesPanel },
            { key: 'service-types', label: 'Service Types', panel: ServiceTypesPanel },
            { key: 'note-types', label: 'Note Types', panel: NoteTypesPanel },
            { key: 'session-types', label: 'Session Types', panel: SessionTypesPanel },
        ],
    },
    {
        key: 'clients',
        label: 'Clients',
        items: [
            { key: 'diagnoses', label: 'Diagnoses', panel: DiagnosesPanel },
            { key: 'client-relationships', label: 'Client Relationships', panel: ParentRelationshipsPanel },
            { key: 'custom-fields-client', label: 'Custom Client Fields', panel: ClientCustomFieldsPanel },
        ],
    },
    {
        key: 'treatment',
        label: 'Treatment',
        items: [
            { key: 'behavior-types', label: 'Behavior Types', panel: BehaviorTypesPanel },
            { key: 'program-domains', label: 'Program Domains', panel: ProgramDomainsPanel },
            { key: 'result-codes', label: 'Trial Result Codes', panel: ResultCodesPanel },
            { key: 'custom-fields-tx-plan', label: 'Custom Treatment Plan Fields', panel: TreatmentPlanCustomFieldsPanel },
        ],
    },
    {
        key: 'billing',
        label: 'Billing',
        items: [
            { key: 'payers', label: 'Payers', panel: PayersPanel },
            { key: 'authorization-limits', label: 'Authorization Limits', panel: AuthorizationLimitsPanel },
            { key: 'submission-settings', label: 'Office Ally Submission', panel: SubmissionSettingsPanel },
            { key: 'invoice-settings', label: 'Invoice Settings', panel: BillingSettingsPanel },
        ],
    },
];

export function ConfigurationPage() {
    const canManage = useAuthStore((s) => s.hasPermission('manage_settings'));

    // Tab state lives in the URL (?group=…&item=…) so deep links and the
    // browser back button after navigating into a sub-editor (e.g. the
    // note-type builder) restore the user to the same group + item they
    // had open.
    const [searchParams, setSearchParams] = useSearchParams();

    const group = useMemo(() => {
        const fromUrl = searchParams.get('group') as GroupKey | null;
        return GROUPS.find((g) => g.key === fromUrl) ?? GROUPS[0];
    }, [searchParams]);

    const selected = useMemo(() => {
        const fromUrl = searchParams.get('item');
        return group.items.find((i) => i.key === fromUrl) ?? group.items[0];
    }, [group, searchParams]);

    const activeGroup = group.key;
    const activeItem = selected?.key;

    const setGroup = (next: GroupKey) => {
        const target = GROUPS.find((g) => g.key === next);
        const firstItem = target?.items[0]?.key;
        const params = new URLSearchParams();
        params.set('group', next);
        if (firstItem) params.set('item', firstItem);
        // replace:true so back button skips intermediate tab clicks and
        // only steps back to before the user arrived at the page.
        setSearchParams(params, { replace: true });
    };

    const setItem = (next: string) => {
        const params = new URLSearchParams(searchParams);
        params.set('group', activeGroup);
        params.set('item', next);
        setSearchParams(params, { replace: true });
    };

    const ActivePanel = selected?.panel;

    if (!canManage) {
        return (
            <div className="p-6 max-w-[1100px] mx-auto">
                <h1 className="font-display text-2xl font-bold text-text mb-1">Configuration</h1>
                <p className="text-sm text-text-muted">
                    You don't have permission to manage practice configuration.
                </p>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-[1200px] mx-auto">
            <div className="mb-1">
                <h1 className="font-display text-2xl font-bold text-text">Configuration</h1>
            </div>
            <p className="text-xs text-text-subtle mb-4">
                Practice-wide reference data — types, domains, and codes used across
                clinical, scheduling, and billing workflows.
            </p>

            <div className="flex gap-1 border-b border-border mb-5" role="tablist" aria-label="Configuration groups">
                {GROUPS.map((g) => (
                    <button
                        key={g.key}
                        type="button"
                        role="tab"
                        aria-selected={activeGroup === g.key}
                        onClick={() => setGroup(g.key)}
                        className={cn(
                            'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                            activeGroup === g.key
                                ? 'text-text border-accent'
                                : 'text-text-muted border-transparent hover:text-text',
                        )}
                    >
                        {g.label}
                    </button>
                ))}
            </div>

            {group.items.length === 0 ? (
                <div className="p-12 text-center text-text-subtle border border-border rounded-lg">
                    {group.placeholder ?? 'Nothing configured here yet.'}
                </div>
            ) : group.items.length === 1 ? (
                <div>{ActivePanel && <ActivePanel />}</div>
            ) : (
                <div className="flex gap-5">
                    <nav
                        aria-label={`${group.label} sections`}
                        className="shrink-0 w-56 border-r border-border pr-3"
                    >
                        <ul className="space-y-0.5">
                            {group.items.map((item) => (
                                <li key={item.key}>
                                    <button
                                        type="button"
                                        onClick={() => setItem(item.key)}
                                        className={cn(
                                            'w-full text-left px-3 py-2 text-sm rounded-md transition-colors',
                                            activeItem === item.key
                                                ? 'bg-surface-muted text-text font-medium'
                                                : 'text-text-muted hover:text-text hover:bg-surface-muted',
                                        )}
                                    >
                                        {item.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </nav>
                    <div className="flex-1 min-w-0">
                        {ActivePanel && <ActivePanel />}
                    </div>
                </div>
            )}
        </div>
    );
}
